import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { IndicativeBadge } from "@/components/IndicativeBadge";
import Footer from "@/components/Footer";
import { MarketSEO } from "@/components/SEO";
import {
  CaretUp, CaretDown, Minus, Storefront, TrendUp, TrendDown, Clock, ArrowRight, Cow,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const FEATURED_CATEGORIES = [
  { id: "bullocks_400_500kg", name: "Bullocks 400-500kg", unit: "€/kg" },
  { id: "heifers_400_500kg", name: "Heifers 400-500kg", unit: "€/kg" },
  { id: "weanlings_300_400kg", name: "Weanlings 300-400kg", unit: "€/kg" },
  { id: "store_lambs", name: "Store Lambs", unit: "per head" },
];

const todayLabel = new Date().toLocaleDateString("en-IE", {
  weekday: "long", day: "numeric", month: "long", year: "numeric",
});

function ChangeBadge({ value }) {
  if (value > 0) return <span className="inline-flex items-center gap-0.5 text-green-600 font-mono text-xs"><CaretUp size={10} weight="fill" />+{value.toFixed(2)}%</span>;
  if (value < 0) return <span className="inline-flex items-center gap-0.5 text-red-600 font-mono text-xs"><CaretDown size={10} weight="fill" />{value.toFixed(2)}%</span>;
  return <span className="inline-flex items-center gap-0.5 text-[#6B6661] font-mono text-xs"><Minus size={10} />0.00%</span>;
}

function CategoryTable({ category, prices }) {
  return (
    <div className="bg-white border border-[#E0D4C8]" data-testid={`category-table-${category.id}`}>
      <div className="flex items-center justify-between px-4 py-3 bg-[#022C22]">
        <h3 className="font-serif text-white text-base">{category.name}</h3>
        <span className="text-[#6EE7B7] font-mono text-[10px] uppercase tracking-wider">{category.unit}</span>
      </div>
      <table className="w-full text-sm">
        <tbody>
          {prices.map((p, i) => (
            <tr key={p.id} className={i % 2 ? "bg-[#FFF9F2]" : "bg-white"}>
              <td className="px-4 py-2.5 text-[#33302E] font-medium">{p.mart_name}</td>
              <td className="px-2 py-2.5 text-[#6B6661] text-xs">{p.county}</td>
              <td className="px-2 py-2.5 text-right font-mono text-[#022C22] font-semibold">
                {category.unit === "€/kg" ? `€${p.price.toFixed(2)}` : `€${Math.round(p.price)}`}
              </td>
              <td className="px-4 py-2.5 text-right"><ChangeBadge value={p.change_percent} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MoverRow({ item }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#E0D4C8] last:border-b-0">
      <div>
        <div className="text-sm font-medium text-[#33302E]">{item.mart_name}</div>
        <div className="text-xs text-[#6B6661]">{item.county} · {item.category.replace(/_/g, " ")}</div>
      </div>
      <div className="text-right">
        <div className="font-mono text-sm text-[#022C22]">€{item.price.toFixed(2)}</div>
        <ChangeBadge value={item.change_percent} />
      </div>
    </div>
  );
}

export default function TodaysMartPricesPage() {
  const [summary, setSummary] = useState(null);
  const [categoryPrices, setCategoryPrices] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [summaryRes, ...catRes] = await Promise.all([
        axios.get(`${API}/prices/irish-marts/summary`),
        ...FEATURED_CATEGORIES.map((c) =>
          axios.get(`${API}/prices/irish-marts`, {
            params: { category: c.id, sort_by: "price_desc", limit: 5 },
          })
        ),
      ]);
      setSummary(summaryRes.data);
      const byCat = {};
      FEATURED_CATEGORIES.forEach((c, i) => {
        byCat[c.id] = (catRes[i].data.prices || []).slice(0, 5);
      });
      setCategoryPrices(byCat);
    } catch (err) {
      console.error("Error fetching today's prices:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const seoCommodities = (categoryPrices.bullocks_400_500kg || []).map((p) => ({
    name: `${p.mart_name} - Bullocks 400-500kg`,
    price: p.price,
    currency: "EUR",
  }));

  return (
    <div className="min-h-screen bg-[#FFF1E5]" data-testid="todays-prices-page">
      <MarketSEO
        title={`Today's Mart Prices - ${todayLabel}`}
        description={`Live Irish mart prices for ${todayLabel}: bullock, heifer, weanling and lamb prices from 90+ livestock marts across Ireland, plus the biggest daily movers. Updated daily.`}
        url="/todays-mart-prices"
        region="Ireland"
        commodities={seoCommodities}
      />
      <Header />

      <section className="bg-gradient-to-r from-[#022C22] to-[#064E3B] py-8">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span className="px-3 py-1 bg-[#DC2626] text-white text-xs font-mono uppercase tracking-wider animate-pulse">Live</span>
            <span className="px-3 py-1 bg-[#FBBF24] text-[#022C22] text-xs font-mono uppercase tracking-wider" data-testid="todays-prices-date">{todayLabel}</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-white mb-2">Today's Mart Prices</h1>
          <div className="mb-2"><IndicativeBadge /></div>
          <p className="text-[#A7F3D0] text-lg">
            Indicative cattle and sheep prices across 90+ Irish livestock marts — live mart feeds are being rolled out
          </p>
        </div>
      </section>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F766E]"></div>
          </div>
        ) : (
          <>
            {summary?.indices?.length > 0 && (
              <section className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
                {summary.indices.map((idx) => (
                  <div key={idx.symbol} className="bg-[#022C22] p-6" data-testid={`index-card-${idx.symbol.toLowerCase()}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[#6EE7B7] font-mono text-xs uppercase tracking-wider">{idx.name}</span>
                      <span className="px-2 py-0.5 bg-[#0F766E] text-white text-[10px] font-mono">{idx.symbol}</span>
                    </div>
                    <div className="flex items-end gap-3">
                      <span className="font-mono text-4xl text-white">
                        {idx.unit === "€/kg" ? `€${idx.value.toFixed(2)}` : `€${Math.round(idx.value)}`}
                      </span>
                      <span className={`font-mono text-sm mb-1 ${idx.change >= 0 ? "text-green-400" : "text-red-400"}`}>
                        {idx.change >= 0 ? "▲" : "▼"} {Math.abs(idx.change).toFixed(2)}%
                      </span>
                    </div>
                    <span className="text-[#A7F3D0] text-xs">{idx.unit}</span>
                  </div>
                ))}
              </section>
            )}

            <section className="mb-10">
              <div className="flex items-center gap-2 mb-6">
                <Storefront size={24} className="text-[#0F766E]" />
                <h2 className="font-serif text-2xl text-[#33302E]">Top Prices by Category</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {FEATURED_CATEGORIES.map((c) => (
                  <CategoryTable key={c.id} category={c} prices={categoryPrices[c.id] || []} />
                ))}
              </div>
            </section>

            <section className="mb-10">
              <div className="flex items-center gap-2 mb-6">
                <Cow size={24} className="text-[#0F766E]" />
                <h2 className="font-serif text-2xl text-[#33302E]">Today's Biggest Movers</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-[#E0D4C8]" data-testid="movers-gainers">
                  <div className="flex items-center gap-2 px-4 py-3 bg-[#022C22]">
                    <TrendUp size={16} className="text-green-400" />
                    <h3 className="font-serif text-white text-base">Top Gainers</h3>
                  </div>
                  {(summary?.top_gainers || []).map((m) => <MoverRow key={m.id} item={m} />)}
                </div>
                <div className="bg-white border border-[#E0D4C8]" data-testid="movers-losers">
                  <div className="flex items-center gap-2 px-4 py-3 bg-[#022C22]">
                    <TrendDown size={16} className="text-red-400" />
                    <h3 className="font-serif text-white text-base">Top Fallers</h3>
                  </div>
                  {(summary?.top_losers || []).map((m) => <MoverRow key={m.id} item={m} />)}
                </div>
              </div>
            </section>

            <section className="bg-[#022C22] p-6 sm:p-8 mb-6">
              <h2 className="font-serif text-xl text-white mb-4">Explore Full Market Data</h2>
              <div className="flex flex-wrap gap-3">
                <Link to="/irish-marts" data-testid="cta-irish-marts" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0F766E] text-white text-sm font-medium hover:bg-[#0D6B64] transition-colors">
                  🇮🇪 All 90+ Irish Marts <ArrowRight size={14} />
                </Link>
                <Link to="/uk-markets" data-testid="cta-uk-markets" className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#0F766E] text-[#6EE7B7] text-sm font-medium hover:bg-[#0F766E]/20 transition-colors">
                  🇬🇧 UK Markets <ArrowRight size={14} />
                </Link>
                <Link to="/usa-markets" data-testid="cta-usa-markets" className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#0F766E] text-[#6EE7B7] text-sm font-medium hover:bg-[#0F766E]/20 transition-colors">
                  🇺🇸 USA Markets <ArrowRight size={14} />
                </Link>
              </div>
            </section>

            {summary?.last_updated && (
              <div className="flex items-center gap-2 text-xs text-[#6B6661]" data-testid="last-updated">
                <Clock size={14} />
                <span>Last updated: {new Date(summary.last_updated).toLocaleString("en-IE")} · Prices from {summary.total_marts || "90+"} marts</span>
              </div>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
